import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const MONTH_LABELS = { "01": "Ene", "02": "Feb", "03": "Mar", "04": "Abr", "05": "May", "06": "Jun", "07": "Jul", "08": "Ago", "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dic" };
const MONTHS = Object.keys(MONTH_LABELS);
const thisYear = new Date().getFullYear();
const YEARS = [thisYear - 2, thisYear - 1, thisYear, thisYear + 1, thisYear + 2];

export function ScheduleAnalyticsView({ clients, defaultClientId }) {
  const [year, setYear] = useState(String(thisYear));
  const [clientId, setClientId] = useState(defaultClientId || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/schedule/analytics", { params: { year, client_id: clientId || undefined } });
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, clientId]);

  const chartData = data ? data.monthly_series.map((m) => ({ month: MONTH_LABELS[m.month], hours: m.total_hours })) : [];

  return (
    <div className="card" style={{ padding: 16, marginBottom: 18 }} data-testid="schedule-analytics-panel">
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14, alignItems: "flex-end" }}>
        <div className="field" style={{ margin: 0, minWidth: 160 }}>
          <label>Objetivo</label>
          <select value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="analytics-filter-client">
            <option value="">Todos los objetivos</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ margin: 0, minWidth: 120 }}>
          <label>Año</label>
          <select value={year} onChange={(e) => setYear(e.target.value)} data-testid="analytics-filter-year">
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        {data && (
          <div style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 8 }} data-testid="analytics-grand-total">
            Total del año: <b style={{ color: "#0A0A0A" }}>{data.grand_total_hours} h</b>
          </div>
        )}
      </div>

      {loading && <div className="skel" style={{ height: 220 }} />}

      {!loading && data && (
        <>
          <div style={{ width: "100%", height: 260, marginBottom: 18 }} data-testid="analytics-bar-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--invict-bone-3)" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fontWeight: 700 }} />
                <YAxis tick={{ fontSize: 11 }} width={40} />
                <Tooltip formatter={(v) => [`${v} h`, "Horas"]} />
                <Bar dataKey="hours" fill="#BF3437" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <h4 style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 800, color: "#0A0A0A" }}>Horas por objetivo, divididas por mes</h4>
          {data.by_client.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }} data-testid="analytics-by-client-empty">Sin turnos cargados en {year}.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, minWidth: 720 }} data-testid="analytics-by-client-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "2px solid var(--invict-bone-3)" }}>Objetivo</th>
                    {MONTHS.map((m) => <th key={m} style={{ textAlign: "right", padding: "6px 6px", borderBottom: "2px solid var(--invict-bone-3)" }}>{MONTH_LABELS[m]}</th>)}
                    <th style={{ textAlign: "right", padding: "6px 8px", borderBottom: "2px solid var(--invict-bone-3)" }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_client.map((c) => (
                    <tr key={c.client_id} data-testid={`analytics-row-${c.client_id}`}>
                      <td style={{ padding: "6px 8px", borderBottom: "1px solid var(--invict-bone-3)", fontWeight: 700 }}>{c.client_name}</td>
                      {MONTHS.map((m) => <td key={m} style={{ textAlign: "right", padding: "6px 6px", borderBottom: "1px solid var(--invict-bone-3)", color: c.months[m] ? "#0A0A0A" : "var(--invict-text-muted)" }}>{c.months[m] || "—"}</td>)}
                      <td style={{ textAlign: "right", padding: "6px 8px", borderBottom: "1px solid var(--invict-bone-3)", fontWeight: 900 }}>{c.total_hours} h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
